import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaderResponse, HttpHeaders } from '@angular/common/http';
import { MyProfileDashboardData, ProfileCompletion, PlayersCurrentTeamData, CountryList, StateList, PlayerBasicInfo, ParentRelationship, PlayerFamilyInfo } from '../models';
import { HighSchoolCity, HighSchool, HighSchoolUserData } from '../models/player/high-school-city';
import { PlayerSoccerInfo } from '../models/player/player-soccer-info';
import { PlayerReferencesData } from '../models/player/player-references';
import { PersonalVideosData } from '../models/player/personal-video-data';
import { CollegesViews } from '../models/player/colleges-viewes';
import { TopMatches } from '../models/player/top-matches';

@Injectable()
export class MyProfileDashboardService {

  private api = '/api/Player';
  private apiClub = '/api/Club';
  private apiTeam = '/api/Team';
  private apiClubParent = '/api/ClubParent';
  links: { title: string; url: any; }[];
  constructor(
    private http: HttpClient
  ) { }

  // Dashboard Page
  getMyProfileDashboardData(playerID: string) {
    return this.http.get<MyProfileDashboardData>(this.api + '/get-myprofile-dashboard-data/' + playerID);
  }

  getProfileCompletionData(playerID: string) {
    return this.http.get<ProfileCompletion>(this.api + '/get-profile-completion-data/' + playerID);
  }

  getPlayersCurrentTeamData(playerID: string, clubID) {
    return this.http.get<PlayersCurrentTeamData>(this.api + '/get-players-teams-data/' + playerID, { params: { clubID } } as any);
  }

  removePlayerFromTeam(playerID: number, teamID: number) {
    return this.http.delete(this.api + '/remove-player-from-team/' + playerID + '/' + teamID);
  }

  getCountries() {
    return this.http.get<CountryList[]>(this.api + '/get-all-countries/');
  }

  getPositionList(sportID: number) {
    return this.http.get<any>(this.apiClubParent + '/get-position-list-by-sportid/' + sportID);
  }

  getStatesByCountryID(countryID: number) {
    return this.http.get<StateList[]>(this.api + '/get-all-states-by-countryId/' + countryID);
  }

  getStates() {
    return this.http.get<StateList[]>(this.api + '/get-all-states/');
  }

  getPlayersBasicInfo(playerID: number, teamID: number) {
    return this.http.get<PlayerBasicInfo>(this.api + '/get-player-basic-info/' + playerID + '/' + teamID);
  }

  updatePlayersBasicInfo(basicInfo: any) {
    return this.http.put(this.api + '/update-player-basic-info', basicInfo, { responseType: 'text' });
  }

  getParentRelationship() {
    return this.http.get<ParentRelationship>(this.api + '/get-parent-relation/');
  }

  getFamilyInfo(playerID: string) {
    return this.http.get<PlayerFamilyInfo>(this.api + '/get-family-info/' + playerID);
  }

  updatePlayersFamilyInfo(familyInfo: any[]) {
    return this.http.post(this.api + '/save-update-family-info', familyInfo, { responseType: 'text' });
  }

  removeOldProfilePhotoFromS3(photos: string[]) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      }),
      body: {
        playerFormPathList: photos
      }
    };
    return this.http.delete(this.api + '/removeFilesFromS3', httpOptions);
  }

  removeParent(userParentChildID: number) {
    return this.http.delete(this.api + '/remove-family-info/' + userParentChildID);
  }

  updateCommitmentStatusData(data: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      })
    };
    return this.http.post(this.api + '/update-commitment-status-data', data, httpOptions);
  }

  getCommitmentStatusData(playerID: number) {
    return this.http.get<any>(this.api + '/get-commitment-status-data-by-playerID/' + playerID);
  }

  getCollegeListByState(state: number, type: string) {
    return this.http.get<any>(this.api + '/get-college-list-by-stateID/' + state + '/' + type);
  }

  getHighSchoolDataByPlayer(playerID: number) {
    return this.http.get<HighSchoolUserData>(this.api + '/get-selected-high-school-info/' + playerID);
  }

  getCityListByState(stateID: number) {
    return this.http.get<HighSchoolCity[]>(this.api + '/get-highschool-city-by-state/' + stateID);
  }

  getHighSchoolListByCity(city: string) {
    return this.http.get<HighSchool[]>(this.api + '/get-highschool-list-by-city/' + city);
  }

  addSchoolEmail(addSchoolData: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      })
    };
    return this.http.post(this.api + '/send-email-to-add-highschool', addSchoolData, httpOptions);
  }

  saveHighSchoolInfo(highSchoolData: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      })
    };
    return this.http.post(this.api + '/save-highschool-user-info', highSchoolData, httpOptions);
  }

  getPlayersSoccerInfoData(playerID: string) {
    return this.http.get<PlayerSoccerInfo>(this.api + '/get-player-soccer-info/' + playerID);
  }

  updatePlayersSoccerInfo(soccerInfo: PlayerSoccerInfo) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      })
    };
    return this.http.post(this.api + '/update-player-soccer-info', soccerInfo, httpOptions);
  }

  getPlayersAcademicInfoData(playerID: string) {
    return this.http.get(this.api + '/get-player-academic-info/' + playerID);
  }

  updatePlayersAcademicInfo(academicInfo: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      })
    };
    return this.http.post(this.api + '/update-player-academic-info', academicInfo, httpOptions);
  }

  getPlayersFitnessInfoData(playerID: string) {
    return this.http.get(this.api + '/get-player-fitness-info/' + playerID);
  }

  updatePlayersFitnessInfo(fitnessInfo: any[]) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      })
    };
    return this.http.post(this.api + '/update-player-fitness-info', fitnessInfo, httpOptions);
  }

  getPlayerPreferencesInfo(playerID: number) {
    return this.http.get<any>(this.api + '/get-player-preferences-info/' + playerID);
  }

  getCollegeDivisionList() {
    return this.http.get<any>(this.api + '/get-college-division-list');
  }

  getCollegeMajorList() {
    return this.http.get<any>(this.api + '/get-college-major-list');
  }

  getCollegeLocationList() {
    return this.http.get<any>(this.api + '/get-college-location-list');
  }

  updatePlayerPreferencesInfo(preferencesData: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      })
    };
    return this.http.post(this.api + '/update-player-preferences-info', preferencesData, httpOptions);
  }

  getPlayersReferencesData(playerID: number) {
    return this.http.get<PlayerReferencesData>(this.api + '/get-references-data/' + playerID);
  }

  updateReferenceStatus(userReferenceID: number, statusID: number) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      })
    };
    return this.http.post(this.api + '/update-reference-status/' + userReferenceID + '/' + statusID, httpOptions);
  }

  deleteSelectedReferences(ids: string) {
    return this.http.delete(this.api + '/delete-user-references-info/' + ids);
  }

  getReferenceDataByUserReferenceID(urID: string) {
    return this.http.get<PlayerReferencesData>(this.api + '/get-reference-data/' + urID);
  }
  addNewReference(referenceData: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      })
    };
    return this.http.post(this.api + '/save-user-references-info', referenceData, httpOptions);
  }

  resendReferenceInvitation(referenceData: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      })
    };
    return this.http.post(this.api + '/resend-reference-invitation', referenceData, httpOptions);
  }

  getPlayersPersonalVideosData(playerID: string) {
    return this.http.get<PersonalVideosData>(this.api + '/get-videos/' + playerID);
  }

  addNewPlayerVideo(videoData: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      })
    };
    return this.http.post(this.api + '/add-player-video', videoData, httpOptions);
  }

  deleteVideoFromS3onCancel(videoList: string[]) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      }),
      body: {
        playerFormPathList: videoList
      }
    };
    return this.http.delete(this.api + '/remove-player-video-files-from-s3', httpOptions);
  }

  deleteSelectedVideos(ids: string) {
    return this.http.delete(this.api + '/delete-player-videos/' + ids);
  }

  getCollegesViewsOfPlayer(playerID: string) {
    return this.http.get<CollegesViews>(this.api + '/get-views-colleges-data/' + playerID);
  }

  getPossibleCollegeMatchList(playerID: number) {
    return this.http.get<TopMatches>(this.api + '/get-possible-college-match-list/' + playerID);
  }

  getPlayerCardDetailInfo(playerID: number, clubID: number) {
    return this.http.get(this.apiClub + '/get-player-card-info/' + playerID + '/' + clubID);
  }

  getClubHealthAndFitnessDashboardData(clubID: number, flag: number, year: number, month: number) {
    return this.http.get(this.apiClub + '/get-health-and-fitness-dashboard-data/' + clubID + '/' + flag + '/' + year + '/' + month);
  }

  getTeamHealthAndFitnessDashboardData(teamID: number) {
    return this.http.get(this.apiTeam + '/get-health-and-fitness-dashboard-data/' + teamID);
  }

  getClubEvaluationDashboardData(clubID: number, year: number, flag: number) {
    return this.http.get(this.apiClub + '/get-club-evaluation-dashboard-data/' + clubID + '/' + flag + '/' + year);
  }

}



// WEBPACK FOOTER //
// ./src/app/services/my-profile-dashboard.service.ts